<template>
  <div :class="controlClasses" class="mds-form-control mds-checkbox">
    <label :for="uid" :style="labelStyles" v-if="label">{{ label }}</label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <input
          :class="inputClass"
          :id="uid"
          ref="checkbox"
          type="checkbox"
          :disabled="disabled"
          @change="onChange"
        />

        <label :for="uid">
          <div class="mds-checkbox-box" />

          <div class="mds-checkbox-box-content">
            <VIcon material="check" />
          </div>

          <!-- @slot Слот для лейбла -->
          <slot />
        </label>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VCheckboxV2.scss" />

<script lang="ts">
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";

import VIcon from "@/lib/components/VIcon/VIcon.vue";

export default defineComponent({
  name: "VCheckboxV2",

  components: { VIcon },

  extends: VBaseControl,

  inheritAttrs: true,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: {
      type:    [Number, String, Boolean, Object, Array],
      default: null,
    },
    /** Отмеченное значение */
    checkedValue: {
      type:    [Number, String, Boolean, Object, Array],
      default: null,
    },
    /** Неотмеченное значение */
    uncheckedValue: {
      type:    [Number, String, Boolean, Object, Array],
      default: null,
    },
    /** Реактивный источник для инициализации значения, напр. () => company.taxSystem */
    dependsOn: { type: Function, default: null },
    /** Извлекает значение из источника, напр. (taxSystem) => taxSystem.id !== 4 */
    default:   { type: Function, default: null },
  },

  emits: ["input", "update:modelValue"],

  computed: {
    inputClass() {
      return { checked: this.isChecked };
    },

    /**
     * Стиль для `.inner`. Если задана labelWidth — оставшаяся ширина после лейбла.
     * Контролы со своей спецификой (например, text-align) могут переопределить
     * computed и расширить базовый.
     */
    innerStyles(): Record<string, string> | null {
      if (this.labelWidth === null) return null;
      return {
        width: `calc(100% - ${this.labelWidth})`,
      };
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      return this.getBaseControlClasses();
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    /** Проверка отмеченности */
    isChecked(): boolean {
      if (this.checkedValue !== null) {
        return Core.utils.isEqual(this.modelValue, this.checkedValue);
      }
      return Boolean(this.modelValue);
    },
  },

  watch: {
    modelValue(newVal, oldVal) {
      this.validation.markTouched();

      if (Core.utils.isEqual(newVal, oldVal) === false) {
        this.validation.markDirty();
        this.validate();
      }

      this.syncInputChecked();
    },
  },

  created() {
    /** Смена источника выставляет значение по default — как в VInputV2 */
    if (this.dependsOn !== null && this.default !== null) {
      this.$watch(this.dependsOn, (source) => {
        if (Core.utils.isNullOrUndefined(source) === false) {
          const value = this.default(source);

          this.$emit("input", value);
          this.$emit("update:modelValue", value);
        }
      });
    }
  },

  mounted() {
    this.syncInputChecked();
  },

  methods: {
    onChange(event) {
      let value = event.target.checked;
      if (this.checkedValue !== null) {
        value = value === true ? this.checkedValue : this.uncheckedValue;
      }
      this.$emit("input", value);
      this.$emit("update:modelValue", value);
    },

    /**
     * Синхронизирует нативный input.checked с текущим modelValue.
     * Нужно, потому что свойство checked нативного чекбокса не управляется
     * через :checked в шаблоне (Vue не делает диффа этого атрибута стабильно
     * при кастомных checkedValue), приходится дёргать DOM напрямую.
     */
    syncInputChecked() {
      const input = this.$refs.checkbox;
      if (input === null || input === undefined) return;
      input.checked = this.isChecked;
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     *
     * Для чекбокса required нельзя проверять стандартным способом — false
     * это валидное значение. На чекбоксе required означает "должен быть
     * в положении checked". Дописываем правило вручную.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;

      await this.validation.validate(this.modelValue);

      if (this.required === true) {
        this.validateRequired();
      }
    },

    /**
     * Дописывает к стандартной валидации правило required для чекбокса.
     * Если ошибки уже есть — не дублируем required.
     */
    validateRequired(): void {
      if (this.validation.errors.length > 0) return;

      if (this.isInUncheckedState() === true) {
        this.validation.errors.push("Поле обязательно к заполнению");
        this.validation.isValid = false;
      }
    },

    /**
     * Чекбокс сейчас в положении "не отмечено"?
     *
     * Случай 1: uncheckedValue === null — считаем неотмеченным, если modelValue
     *           falsy (null, undefined, false, 0).
     * Случай 2: uncheckedValue задано явно — сравниваем modelValue с ним через
     *           isEqual (на случай объектов/массивов).
     */
    isInUncheckedState(): boolean {
      if (this.uncheckedValue === null) {
        return Boolean(this.modelValue) === false;
      }
      return Core.utils.isEqual(this.modelValue, this.uncheckedValue);
    },
  },
});
</script>